"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useState } from "react";
import AllergiesDialog from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/dialogs/allergies-dialog";
import FamilyHistoryDialog from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/dialogs/family-history-dialog";
import MedicalHistoryDialog from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/dialogs/medical-history-dialog";
import PatientMedicationsDialog from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/dialogs/patient-medications-dialog";
import { Button } from "@/components/ui";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { useUser } from "@/contexts/user-provider";
import { useHasAnyLabVendor } from "@/hooks/use-lab-vendor-modules";
import { getAllergies, getFamilyHistory, getMedicalHistory, getMedications, getSocialHistory } from "@/lib/patient-health-fields";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import type { PatientChart } from "@/types/patient";
import AllergiesSection from "./sections/allergies-section";
import { FamilyHistorySection, MedicalHistorySection, SocialHistorySection } from "./sections/health-history-section";
import LabsSection from "./sections/labs-section";
import PastDosesSection from "./sections/past-doses-section";
import PatientReportedMedicationsSection from "./sections/patient-reported-medications-section";
import PreviousNotesSection from "./sections/previous-notes-section";
import VitalsSection from "./sections/vitals-section";
import { SidebarSectionHeader } from "./sidebar-section-header";
const ALL_SECTIONS = ["allergies", "patient-reported-medications", "past-doses", "medical-history", "family-history", "social-history", "vitals", "labs", "previous-notes"];
function ChartSection(t0) {
  const $ = _c(10);
  const {
    value,
    title,
    count,
    action,
    children
  } = t0;
  let t1;
  if ($[0] !== action || $[1] !== count || $[2] !== title) {
    t1 = <SidebarSectionHeader title={title} count={count} action={action} />;
    $[0] = action;
    $[1] = count;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children) {
    t2 = <AccordionContent>{children}</AccordionContent>;
    $[4] = children;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== value) {
    t3 = <AccordionItem value={value} className="py-1">{t1}{t2}</AccordionItem>;
    $[6] = t1;
    $[7] = t2;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
interface Props {
  patient: PatientChart;
  /** The note open in the editor, left out of "Previous Notes". */
  currentNoteId?: string;
}

/**
 * The patient sidebar while charting: the clinical record a provider reads
 * while writing a note, in place of the front-desk sections.
 */
export default function MedicalChart(t0) {
  const $ = _c(79);
  const {
    patient,
    currentNoteId
  } = t0;
  const {
    currentUser
  } = useUser();
  let t1;
  if ($[0] !== currentUser) {
    t1 = hasPermission(currentUser, PERMISSIONS.CLINICAL_NOTES_READ);
    $[0] = currentUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canViewNotes = t1;
  const hasLabVendor = useHasAnyLabVendor();
  let t2;
  if ($[2] !== currentUser || $[3] !== hasLabVendor) {
    t2 = hasLabVendor && hasPermission(currentUser, PERMISSIONS.LABS_READ);
    $[2] = currentUser;
    $[3] = hasLabVendor;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const canViewLabs = t2;
  const [expanded, setExpanded] = useState(ALL_SECTIONS);
  let t3;
  if ($[5] !== patient) {
    t3 = getAllergies(patient);
    $[5] = patient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Button variant="ghost" className="p-1 text-primary">Edit</Button>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== patient.id) {
    t5 = <AllergiesDialog patientId={patient.id}>{t4}</AllergiesDialog>;
    $[8] = patient.id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== patient) {
    t6 = <AllergiesSection patient={patient} />;
    $[10] = patient;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3.length || $[13] !== t5 || $[14] !== t6) {
    t7 = <ChartSection value="allergies" title="Allergies" count={t3.length} action={t5}>{t6}</ChartSection>;
    $[12] = t3.length;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== patient) {
    t8 = getMedications(patient);
    $[16] = patient;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Button variant="ghost" className="p-1 text-primary">Edit</Button>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== patient.id) {
    t10 = <PatientMedicationsDialog patientId={patient.id}>{t9}</PatientMedicationsDialog>;
    $[19] = patient.id;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== patient) {
    t11 = <PatientReportedMedicationsSection patient={patient} />;
    $[21] = patient;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t8.length) {
    t12 = <ChartSection value="patient-reported-medications" title="Patient reported medications" count={t8.length} action={t10}>{t11}</ChartSection>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t8.length;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== currentNoteId || $[28] !== patient) {
    t13 = <ChartSection value="past-doses" title="Past doses"><PastDosesSection patient={patient} currentNoteId={currentNoteId} /></ChartSection>;
    $[27] = currentNoteId;
    $[28] = patient;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== patient) {
    t14 = getMedicalHistory(patient);
    $[30] = patient;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Button variant="ghost" className="p-1 text-primary">Edit</Button>;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== patient.id) {
    t16 = <MedicalHistoryDialog patientId={patient.id}>{t15}</MedicalHistoryDialog>;
    $[33] = patient.id;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== patient) {
    t17 = <MedicalHistorySection patient={patient} />;
    $[35] = patient;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t14.length || $[38] !== t16 || $[39] !== t17) {
    t18 = <ChartSection value="medical-history" title="Medical History" count={t14.length} action={t16}>{t17}</ChartSection>;
    $[37] = t14.length;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== patient) {
    t19 = getFamilyHistory(patient);
    $[41] = patient;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Button variant="ghost" className="p-1 text-primary">Edit</Button>;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== patient.id) {
    t21 = <FamilyHistoryDialog patientId={patient.id}>{t20}</FamilyHistoryDialog>;
    $[44] = patient.id;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== patient) {
    t22 = <FamilyHistorySection patient={patient} />;
    $[46] = patient;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t19.length || $[49] !== t21 || $[50] !== t22) {
    t23 = <ChartSection value="family-history" title="Family History" count={t19.length} action={t21}>{t22}</ChartSection>;
    $[48] = t19.length;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== patient) {
    t24 = getSocialHistory(patient);
    $[52] = patient;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== patient) {
    t25 = <SocialHistorySection patient={patient} />;
    $[54] = patient;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t24.length || $[57] !== t25) {
    t26 = <ChartSection value="social-history" title="Social History" count={t24.length}>{t25}</ChartSection>;
    $[56] = t24.length;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== patient.id) {
    t27 = <ChartSection value="vitals" title="Vitals"><VitalsSection patientId={patient.id} /></ChartSection>;
    $[59] = patient.id;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== canViewLabs || $[62] !== patient.id) {
    t28 = canViewLabs && <ChartSection value="labs" title="Labs"><LabsSection patientId={patient.id} /></ChartSection>;
    $[61] = canViewLabs;
    $[62] = patient.id;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== canViewNotes || $[65] !== currentNoteId || $[66] !== patient.id) {
    t29 = canViewNotes && <ChartSection value="previous-notes" title="Previous Notes"><PreviousNotesSection patientId={patient.id} currentNoteId={currentNoteId} /></ChartSection>;
    $[64] = canViewNotes;
    $[65] = currentNoteId;
    $[66] = patient.id;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== expanded || $[69] !== t12 || $[70] !== t13 || $[71] !== t18 || $[72] !== t23 || $[73] !== t26 || $[74] !== t27 || $[75] !== t28 || $[76] !== t29 || $[77] !== t7) {
    t30 = <Accordion type="multiple" className="mt-2 px-4 pb-6" value={expanded} onValueChange={setExpanded}>{t7}{t12}{t13}{t18}{t23}{t26}{t27}{t28}{t29}</Accordion>;
    $[68] = expanded;
    $[69] = t12;
    $[70] = t13;
    $[71] = t18;
    $[72] = t23;
    $[73] = t26;
    $[74] = t27;
    $[75] = t28;
    $[76] = t29;
    $[77] = t7;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  return t30;
}